import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Heading, Select, Flex, Button, Input, useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { io } from 'socket.io-client';

import GenericTable from '../components/GenericTable';
import { getPlayerWiseSaleReport } from '../actions/playerWiseReportingActions';
import { fetchCompanies } from '../actions/companyActions';
import CompanySelect from '../components/CompanySelect';
import PlayerActionButtons from '../components/PlayerActionButtons';
import { formatDateLocal, getLocalDateRangeForPlayerWise } from '../utils/reportDateRange';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const base_url = process.env.REACT_APP_API_URL_ADMIN;
const REFRESH_INTERVAL_MS = 60000;

const filterOptions = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Custom Range',
];

const ResponsiveDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <Input
    ref={ref}
    value={value || ''}
    onClick={onClick}
    placeholder={placeholder}
    readOnly
    width="100%"
    bg="white"
  />
));

ResponsiveDateInput.displayName = 'ResponsiveDateInput';

const normalizeCompanyKey = (value) => String(value || '').trim().toLowerCase();

const PlayerWiseSaleReport = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const socketRef = useRef(null);
  const { filterColumns } = useFieldPrivilege();

  const { data, loading, error } = useSelector((state) => state.playerSaleReport);
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);

  const isSuperAdmin = user?.role === 'super_admin';

  const [filter, setFilter] = useState('Today');
  const [customRange, setCustomRange] = useState([null, null]);
  const [companyId, setCompanyId] = useState('');
  const [showCancelColumns, setShowCancelColumns] = useState(false);

  // base (unshifted) range
  const [baseStartDate, baseEndDate] = useMemo(() => {
    return filter === 'Custom Range' ? customRange : getLocalDateRangeForPlayerWise(filter);
  }, [filter, customRange]);

  // use selected date range as-is
  const [startDate, endDate] = useMemo(() => {
    if (!baseStartDate || !baseEndDate) return [null, null];
    return [baseStartDate, baseEndDate];
  }, [baseStartDate, baseEndDate]);

  const companyQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeCompanyKey(params.get('comp'));
  }, [location.search]);

  useEffect(() => {
    if (isSuperAdmin) dispatch(fetchCompanies());
  }, [dispatch, isSuperAdmin]);

  // Non–super-admin: use company from login (no company.view privilege needed).
  useEffect(() => {
    if (isSuperAdmin) return;
    const id = user?.companyId || user?.company_id;
    if (id) setCompanyId(String(id));
  }, [isSuperAdmin, user?.companyId, user?.company_id]);

  useEffect(() => {
    if (isSuperAdmin || !user?.companySlug || !companies.length) return;
    const match = companies.find((c) => c.slug === user.companySlug);
    if (match?._id) setCompanyId(String(match._id));
  }, [isSuperAdmin, user?.companySlug, companies]);

  useEffect(() => {
    if (!companies.length || !companyQuery) return;
    const match = companies.find((company) => {
      const slugValue = normalizeCompanyKey(company.slug);
      const nameValue = normalizeCompanyKey(company.name);
      return slugValue === companyQuery || nameValue === companyQuery;
    });
    if (match?._id) {
      setCompanyId(String(match._id));
    }
  }, [companies, companyQuery]);

  useEffect(() => {
    if (!isSuperAdmin || !companies.length) return;

    const params = new URLSearchParams(location.search);
    const selectedCompany = companies.find((company) => String(company._id) === String(companyId));
    const nextCompanyQuery = selectedCompany
      ? normalizeCompanyKey(selectedCompany.slug || selectedCompany.name)
      : '';

    if (nextCompanyQuery) {
      if (companyQuery === nextCompanyQuery) return;
      params.set('comp', nextCompanyQuery);
    } else {
      if (!params.has('comp')) return;
      params.delete('comp');
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [companyId, companies, companyQuery, isSuperAdmin, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    dispatch(getPlayerWiseSaleReport({ startDate, endDate, companyId }));
  }, [dispatch, startDate, endDate, companyId]);

  // socket
  useEffect(() => {
    socketRef.current = io(base_url, { withCredentials: true });

    socketRef.current.on('connect', () => {
      console.log('✅ Socket connected:', socketRef.current.id);
    });
    socketRef.current.on('disconnect', () => {
      console.log('❌ Socket disconnected');
    });

    return () => {
      socketRef.current && socketRef.current.disconnect();
    };
  }, []);

  const refreshData = useCallback((silent = false) => {
    if (startDate && endDate && companyId) {
      dispatch(getPlayerWiseSaleReport({ startDate, endDate, companyId, silent }));
    }
  }, [dispatch, startDate, endDate, companyId]);

  // Periodic soft refresh (no full page reload)
  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    const interval = setInterval(() => refreshData(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [startDate, endDate, companyId, refreshData]);

  // live update via socket
  useEffect(() => {
    const handleUpdate = () => refreshData(true);

    if (socketRef.current) {
      socketRef.current.off('reportUpdate');
      socketRef.current.on('reportUpdate', handleUpdate);
    }

    return () => {
      socketRef.current && socketRef.current.off('reportUpdate');
    };
  }, [refreshData]);

  const getRowStyle = useCallback(() => {
    return {
      backgroundColor: '#00ff00',
    };
  }, []);

  const columns = useMemo(() => [
    { Header: 'Player', accessor: '_id' },
    ...(showCancelColumns ? [
      {
        Header: 'Sales With Cancel',
        accessor: 'salesWithCancel',
        Cell: ({ row }) => {
          const playerId = row.original._id;
          const ticketId = row.original.latest_ticket_id || '0';
          const fDate = startDate ? formatDateLocal(startDate) : '';
          const tDate = endDate ? formatDateLocal(endDate) : '';
          const to = `/${slug || 'root'}/reports/tktdetails?UserID=${encodeURIComponent(playerId)}&TicketID=${encodeURIComponent(ticketId)}&FDate=${fDate}&TDate=${tDate}`;
          return (
            <Link to={to} style={{ color: 'blue', textDecoration: 'underline' }}>
              {row.original.salesWithCancel}
            </Link>
          );
        }
      },
      { Header: 'Cancel', accessor: 'cancelled' },
    ] : []),
    {
      Header: 'Sales',
      accessor: 'Sales',
      Cell: ({ row }) => {
        const playerId = row.original._id;
        const ticketId = row.original.latest_ticket_id || '0';

        // Keep link dates aligned with selected filter range
        const fDate = startDate ? formatDateLocal(startDate) : '';
        const tDate = endDate ? formatDateLocal(endDate) : '';

        const to = `/${slug || 'root'}/reports/tktdetails?UserID=${encodeURIComponent(playerId)}&TicketID=${encodeURIComponent(ticketId)}&FDate=${fDate}&TDate=${tDate}`;
        return (
          <Link to={to} style={{ color: 'blue', textDecoration: 'underline' }}>
            {row.original.Sales}
          </Link>
        );
      }
    },
    { Header: 'Win', accessor: 'win' },
    { Header: 'Gross', accessor: 'gross' },
    { Header: 'Player Comm', accessor: 'playerComm' },
    { Header: 'N2P', accessor: 'n2p' },
    { Header: 'Perc', accessor: 'current_perc' },
    { Header: 'Balance', accessor: 'balance' },
    { Header: 'C/M2p', accessor: 'target_perc' },
    {
      Header: 'Actions',
      Cell: ({ row }) => (
        row.original.type === 'player' ? (
          <PlayerActionButtons player={row.original} refresh={refreshData} />
        ) : null
      ),
    },
  ], [refreshData, showCancelColumns, startDate, endDate, slug]);

  const visibleColumns = useMemo(
    () => filterColumns('reportPlayerWise', columns),
    [columns, filterColumns]
  );

  return (
    <Box>
      <Heading mb={4}>Player Report</Heading>

      <Flex
        gap={3}
        mb={4}
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        wrap="wrap"
      >
        {isSuperAdmin && (
          <CompanySelect value={companyId} onChange={setCompanyId} width="100%" maxW={{ base: '100%', md: '220px' }} />
        )}
        <Select
          width="100%"
          maxW={{ base: '100%', md: '220px' }}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {filterOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Select>
        <Button
          colorScheme={showCancelColumns ? 'orange' : 'blue'}
          onClick={() => setShowCancelColumns((prev) => !prev)}
          width={{ base: '100%', md: 'auto' }}
          minW={{ md: '140px' }}
        >
          {showCancelColumns ? 'Hide Cancel' : 'Show Cancel'}
        </Button>

        {filter === 'Custom Range' && (
          <Flex
            gap={3}
            direction={{ base: 'column', sm: 'row' }}
            width={{ base: '100%', md: 'auto' }}
            flexWrap="wrap"
          >
            <Box width={{ base: '100%', sm: '220px' }}>
              <DatePicker
                selected={customRange[0]}
                onChange={(date) => setCustomRange([date, customRange[1]])}
                placeholderText="Start Date"
                selectsStart
                startDate={customRange[0]}
                endDate={customRange[1]}
                customInput={<ResponsiveDateInput placeholder="Start Date" />}
              />
            </Box>
            <Box width={{ base: '100%', sm: '220px' }}>
              <DatePicker
                selected={customRange[1]}
                onChange={(date) => setCustomRange([customRange[0], date])}
                placeholderText="End Date"
                selectsEnd
                startDate={customRange[0]}
                endDate={customRange[1]}
                minDate={customRange[0]}
                customInput={<ResponsiveDateInput placeholder="End Date" />}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      {loading ? (
        <Box>Loading...</Box>
      ) : error ? (
        <Box>Error: {error}</Box>
      ) : !companyId ? (
        <Box>
          {isSuperAdmin
            ? 'Please select a company to view this report.'
            : 'Unable to resolve your company. Please sign in again.'}
        </Box>
      ) : Array.isArray(data) && data.length > 0 ? (
        <GenericTable
          columns={visibleColumns}
          data={data}
          getRowStyle={getRowStyle}
          tbodyBorder="2px solid black"
          showColumnFilters={false}
          compactOnMobile
        />
      ) : (
        <Box>No data available for this period.</Box>
      )}
    </Box>
  );
};

export default PlayerWiseSaleReport;
